import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SelectInput, SelectItem } from '../ahkinia/Select/select';
import { cn } from '@/shared/lib/cn';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  className?: string;
  render: (row: T) => React.ReactNode;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  firstItemOnPage?: number;
  lastItemOnPage?: number;
}

interface DataTableProps<T> {
  title: string;
  titleIcon?: React.ReactNode;
  subtitle?: string;
  rows: T[];
  columns: DataTableColumn<T>[];
  getRowKey: (row: T) => string;
  className?: string;
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Shown inside the table body (below headers) when `rows` is empty. Defaults to a generic "No hay registros disponibles." message. */
  emptyMessage?: React.ReactNode;
  /** When set, interactive pagination controls receive `${testIdPrefix}-pagination-*` test ids. Omit to render no test ids (default). */
  testIdPrefix?: string;
}

export function DataTable<T>({
  title,
  titleIcon,
  subtitle,
  rows,
  columns,
  getRowKey,
  className,
  pagination,
  onPageChange,
  onPageSizeChange,
  emptyMessage,
  testIdPrefix,
}: DataTableProps<T>) {
  const gridTemplateColumns = columns
    .map((col) => (col.key === 'actions' ? 'auto' : 'minmax(13rem, 1fr)'))
    .join(' ');

  return (
    <div
      className={cn(
        '@container rounded-xl overflow-hidden bg-background border border-outline',
        className,
      )}
    >
      {/* Category header */}
      <div className="px-5 py-4 flex items-center gap-2 border-b border-outline bg-select-background">
        <div className="flex gap-4 align-items-center">
          {titleIcon && <span className="shrink-0">{titleIcon}</span>}
          <h3 className="text-[0.875rem] font-semibold text-primary tracking-[0.08em]">{title}</h3>
        </div>

        {subtitle && (
          <span className="ml-auto px-2 py-0.5 rounded-full text-[0.625rem] bg-section-icon-background text-primary-active">
            {subtitle}
          </span>
        )}
      </div>

      {/* Single grid: header + rows share the same column tracks via subgrid */}
      <div className="overflow-x-auto">
        <div className="grid" style={{ gridTemplateColumns }}>
          {/* Column headers */}
          <div className="col-span-full grid grid-cols-subgrid px-5 py-2 bg-select-background border-b border-outline">
            {columns.map((col) => (
              <span
                key={col.key}
                className={cn('text-[0.75rem] text-primary font-semibold', col.className)}
              >
                {col.header}
              </span>
            ))}
          </div>

          {/* Rows — only rendered when there is data */}
          {rows.length > 0 &&
            rows.map((row) => (
              <div
                key={getRowKey(row)}
                className="col-span-full grid grid-cols-subgrid px-5 py-3.5 items-center border-b border-outline hover:bg-select-background transition-colors"
              >
                {columns.map((col) => (
                  <div
                    key={col.key}
                    className={cn(
                      'text-primary-active text-sm [&_*]:text-[0.875rem]',
                      col.className,
                    )}
                  >
                    {col.render(row)}
                  </div>
                ))}
              </div>
            ))}

          {/* Empty state — inside scroll grid as full-span row, but visually pinned
              to viewport via sticky+container-query so it stays centered/static
              while horizontal scroll on header still works. */}
          {rows.length === 0 && (
            <div className="col-span-full border-b border-outline">
              <div className="sticky left-0 w-[100cqw] flex flex-col items-center justify-center py-14 gap-3">
                {emptyMessage ?? (
                  <span className="text-primary text-sm">No hay registros disponibles.</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-outline">
          <div className="flex items-center gap-2">
            <span className="text-xs text-primary min-w-max">
              Mostrando {pagination.firstItemOnPage ?? 1} -{' '}
              {pagination.lastItemOnPage ?? rows.length} de {pagination.totalCount}
            </span>
            <SelectInput
              value={pagination.pageSize ? String(pagination.pageSize) : undefined}
              onValueChange={(value) => onPageSizeChange?.(Number(value))}
              className="w-20 ml-4 px-2 py-1 rounded text-xs bg-background border border-outline text-primary"
              data-testid={testIdPrefix ? `${testIdPrefix}-pagination-page-size` : undefined}
            >
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectInput>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={!pagination.hasPreviousPage}
              data-testid={testIdPrefix ? `${testIdPrefix}-pagination-prev` : undefined}
              className={cn(
                'p-1 rounded hover:bg-select-background transition-colors',
                !pagination.hasPreviousPage && 'opacity-40 cursor-not-allowed',
              )}
            >
              <ChevronLeft size={16} className="text-primary" />
            </button>
            <span className="text-xs text-primary">
              {pagination.page} / {pagination.totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={!pagination.hasNextPage}
              data-testid={testIdPrefix ? `${testIdPrefix}-pagination-next` : undefined}
              className={cn(
                'p-1 rounded hover:bg-select-background transition-colors',
                !pagination.hasNextPage && 'opacity-40 cursor-not-allowed',
              )}
            >
              <ChevronRight size={16} className="text-primary" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
